import { Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { getRemoteConfigState } from '../../store';
import { RemoteConfigState } from '../../store/remote-config/remote-config.reducer';

export interface IRemoteConfigDirectiveCheck {
  key: string;
  expected: any;
}

/**
 * If a string ("key" is passed in - the assumption is { key: '{key}', expected: true }
 */
export type RemoteConfigDirectiveCheck = IRemoteConfigDirectiveCheck | string;

/**
 * Only displays the element if a provided key/value exists within the
 * store. Default store values are updated from a Firebase subscription.
 *
 * Usage:
 *
 * 1. Within your .ts file, import the RemoteConfigDirectiveCheck:
 * > import { RemoteConfigDirectiveCheck } from '.../remote-config.directive';
 *
 * 2. Create an object describing the key/values expected:
 * > testConfigItem: RemoteConfigDirectiveCheck =
 * >   { key: 'show_config_list', expected: false };
 *
 * 3. In your html, the directive can now be used:
 * >  <div *remoteConfig="testConfigItem">
 * >    Hello world!
 * >  </div>
 *
 * Or (for shorthand)
 * >  <div *remoteConfig="'some_remote_config_key'">
 * >    Hello world!
 * >  </div>
 */
@Directive({ selector: '[remoteConfig]' })
export class RemoteConfigDirective implements OnDestroy {
  _rendered: boolean;
  remoteConfigItems: RemoteConfigState;
  subscription: any;
  isSatisfied = false;

  constructor(
    public store: Store<any>,
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef
  ) {
    this.subscription = this.store.pipe(select(getRemoteConfigState)).subscribe((config) => {
      this.remoteConfigItems = <any>{
        ...(config || {}),
      };
      this.updateView();
    });
  }

  private _condition: IRemoteConfigDirectiveCheck;
  get condition() {
    return this._condition;
  }

  // If a string ("key" is passed in - the assumption is { key: '{key}', expected: true }
  @Input() set remoteConfig(condition: RemoteConfigDirectiveCheck) {
    if (typeof condition === 'string') {
      this._condition = {
        key: condition,
        expected: true,
      };
    } else {
      this._condition = <any>{ ...(condition || {}) };
    }
    this.updateView();
  }

  private updateView() {
    const config = this.remoteConfigItems || {};
    const { expected, key } = this.condition || { key: '', expected: true };

    if (config[key] === expected && !this._rendered) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      this._rendered = true;
    } else if (config[key] !== expected && this._rendered) {
      this.viewContainerRef.clear();
      this._rendered = false;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
